/**
 * Système de badges Teach'r - critères et récompenses par niveau
 */

export type BadgeLevelId =
  | "explorateur"
  | "bronze"
  | "argent"
  | "or"
  | "diamant"
  | "diamant_plus"
  | "diamant_plus_plus";

export interface BadgeLevel {
  id: BadgeLevelId;
  label: string;
  order: number;
  /** Nombre d'élèves */
  minStudents: number;
  /** Nombre d'heures déclarées */
  minDeclaredHours: number;
  /** Mois de déclaration d'affilée (max 2 mois inactivité été) */
  minConsecutiveMonths: number;
  /** Volume de cours généré (€) */
  minCourseVolume: number;
  /** Nombre de profs parrainés */
  minSponsoredTeachers: number;
  /** VA générée par les filleuls cumulée (€) */
  minVaFromSponsored: number;
  /** Parrainé autres métiers avec Declarons (0 ou 1) */
  minDeclaronsSponsored: number;
  /** CA généré pour Teach'r (€) */
  revenueForTeachr: number;
  /** Récompense obtenue */
  reward: string;
}

export const BADGE_LEVELS: BadgeLevel[] = [
  {
    id: "explorateur",
    label: "Explorateur",
    order: 1,
    minStudents: 1,
    minDeclaredHours: 1,
    minConsecutiveMonths: 1,
    minCourseVolume: 1,
    minSponsoredTeachers: 0,
    minVaFromSponsored: 0,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 1,
    reward: "Tuto pour de nouveaux élèves",
  },
  {
    id: "bronze",
    label: "Médaille de bronze",
    order: 2,
    minStudents: 3,
    minDeclaredHours: 5,
    minConsecutiveMonths: 2,
    minCourseVolume: 200,
    minSponsoredTeachers: 0,
    minVaFromSponsored: 0,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 20.8,
    reward: "Site internet avec sitefacile",
  },
  {
    id: "argent",
    label: "Médaille d'argent",
    order: 3,
    minStudents: 10,
    minDeclaredHours: 50,
    minConsecutiveMonths: 4,
    minCourseVolume: 2000,
    minSponsoredTeachers: 1,
    minVaFromSponsored: 200,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 228.8,
    reward:
      "Mise en place d'une campagne de publicité sur mesure pour promouvoir votre activité sur nos réseaux sociaux Insta & Fb (+23K abonnés) avec budget de 30€",
  },
  {
    id: "or",
    label: "Médaille d'or",
    order: 4,
    minStudents: 20,
    minDeclaredHours: 300,
    minConsecutiveMonths: 9,
    minCourseVolume: 15000,
    minSponsoredTeachers: 3,
    minVaFromSponsored: 1500,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 1716,
    reward:
      "Budget 150€ de pub ou carte cadeau Amazon valeur 150€ ou rétro-commission de 150€ au choix. Si choix pub, option nouvelle créa en vidéo",
  },
  {
    id: "diamant",
    label: "Diamant",
    order: 5,
    minStudents: 30,
    minDeclaredHours: 500,
    minConsecutiveMonths: 18,
    minCourseVolume: 50000,
    minSponsoredTeachers: 5,
    minVaFromSponsored: 10000,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 6240,
    reward: "% réduction des frais de 2% (voir le coût de cette opération)",
  },
  {
    id: "diamant_plus",
    label: "Diamant +",
    order: 6,
    minStudents: 30,
    minDeclaredHours: 500,
    minConsecutiveMonths: 18,
    minCourseVolume: 100000,
    minSponsoredTeachers: 5,
    minVaFromSponsored: 10000,
    minDeclaronsSponsored: 0,
    revenueForTeachr: 9625,
    reward: "-2,5% supplémentaire (réduction des frais)",
  },
  {
    id: "diamant_plus_plus",
    label: "Diamant ++",
    order: 7,
    minStudents: 30,
    minDeclaredHours: 500,
    minConsecutiveMonths: 18,
    minCourseVolume: 200000,
    minSponsoredTeachers: 5,
    minVaFromSponsored: 10000,
    minDeclaronsSponsored: 1,
    revenueForTeachr: 14868,
    reward: "-3% supplémentaires (réduction des frais)",
  },
];

export interface BadgeStats {
  students?: number;
  declaredHours?: number;
  consecutiveMonths?: number;
  courseVolume?: number;
  sponsoredTeachers?: number;
  vaFromSponsored?: number;
  declaronsSponsored?: number;
}

/** Retourne le niveau de badge atteint selon les stats (le plus élevé dont les critères sont remplis) */
export function getBadgeLevelFromStats(stats: BadgeStats): BadgeLevel {
  const s = stats;
  for (let i = BADGE_LEVELS.length - 1; i >= 0; i--) {
    const level = BADGE_LEVELS[i];
    if (
      (s.students ?? 0) >= level.minStudents &&
      (s.declaredHours ?? 0) >= level.minDeclaredHours &&
      (s.consecutiveMonths ?? 0) >= level.minConsecutiveMonths &&
      (s.courseVolume ?? 0) >= level.minCourseVolume &&
      (s.sponsoredTeachers ?? 0) >= level.minSponsoredTeachers &&
      (s.vaFromSponsored ?? 0) >= level.minVaFromSponsored &&
      (s.declaronsSponsored ?? 0) >= level.minDeclaronsSponsored
    ) {
      return level;
    }
  }
  return BADGE_LEVELS[0];
}
